import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import Game from './Game.tsx';
import './index.css';
import Modal from 'react-modal';
import { KeyValueStore } from './KeyValueStore.ts';
import { KeyValueStoreNative } from './KeyValueStoreNative.ts';
import {
    RawLocalStateContext,
    KeyValueStoreContext,
    loadState,
    SupabaseClientContext,
    RcAdapterContext,
} from './GlobalState.tsx';
import { setupSentry } from './Sentry.ts';
import { Capacitor } from '@capacitor/core';
import { KeyValueStoreWeb } from './KeyValueStoreWeb.ts';
import { PuzzleIndex, PuzzleIndexContextProvider } from './PuzzleIndex.tsx';
import { createClient } from '@supabase/supabase-js';
import { SessionContextProvider } from './SessionContext.tsx';
import { LoginFlow } from './LoginFlow.tsx';
import { createRCAdapter } from './RcAdapterFactory.ts';
import { SubscriptionProvider } from './SubscriptionContext.tsx';
import { Paywall } from './Paywall.tsx';
import { InGameNotificationProvider } from './InGameNotificationContext.tsx';
import { InGameNotifications } from './InGameNotifications.tsx';
import { InactivityContextProvider } from './InactivityContext.tsx';
import { AdInterfaceContext, AdInterfaceContextProvider, AdInterfaceFactory } from './AdInterface.tsx';

async function setup() {
    const supabaseClient = createClient(process.env.SUPABASE_URL, process.env.SUPABASE_KEY, {
        auth: { detectSessionInUrl: false },
    });
    setupSentry();
    const startTime = Date.now();
    const persistent = await requestPersistentStorage();
    console.info(`Persistent storage granted: ${persistent}`);
    console.info('Setting up the key-value store...');
    const dbStartTime = Date.now();
    const store: KeyValueStore = Capacitor.getPlatform() === 'web' ? new KeyValueStoreWeb() : new KeyValueStoreNative();
    await store.init();
    const dbTime = Date.now() - dbStartTime;
    console.info(`Key-value store setup completed in ${dbTime}ms`);
    console.info('Loading the initial global state...');
    const rawLocalState = await loadState(store);
    const startTimeCache = Date.now();
    console.info('Building the puzzle cache...');
    const puzzleIndex = new PuzzleIndex(store, supabaseClient);
    await puzzleIndex.init();
    const endTimeCache = Date.now() - startTimeCache;
    console.info(`Puzzle cache built in ${endTimeCache}ms with ${puzzleIndex.size()} puzzles`);
    const setupTime = Date.now() - startTime;
    console.info(`Setup completed in ${setupTime}ms`);

    // Clear the existing HTML content
    document.body.innerHTML = '<div id="root"></div>';

    // Render your React component instead
    const root = createRoot(document.getElementById('root'));
    Modal.setAppElement('#root');
    root.render(
        <StrictMode>
            <SupabaseClientContext.Provider value={supabaseClient}>
                <KeyValueStoreContext.Provider value={store}>
                    <RawLocalStateContext.Provider value={rawLocalState}>
                        <InactivityContextProvider>
                            <SessionContextProvider supabaseClient={supabaseClient}>
                                <PuzzleIndexContextProvider puzzleIndex={puzzleIndex}>
                                    <RcAdapterContext.Provider
                                        value={createRCAdapter({
                                            keys: {
                                                web: process.env.REVENUECAT_API_KEY_WEB,
                                                ios: process.env.REVENUECAT_API_KEY_APPLE,
                                                android: process.env.REVENUECAT_API_KEY_ANDROID,
                                            },
                                            initialState: rawLocalState,
                                        })}>
                                        <SubscriptionProvider>
                                            <InGameNotificationProvider>
                                                <AdInterfaceContextProvider>
                                                    <InGameNotifications />
                                                    <Game />
                                                    <Paywall />
                                                    <LoginFlow />
                                                </AdInterfaceContextProvider>
                                            </InGameNotificationProvider>
                                        </SubscriptionProvider>
                                    </RcAdapterContext.Provider>
                                </PuzzleIndexContextProvider>
                            </SessionContextProvider>
                        </InactivityContextProvider>
                    </RawLocalStateContext.Provider>
                </KeyValueStoreContext.Provider>
            </SupabaseClientContext.Provider>
        </StrictMode>
    );
}

async function requestPersistentStorage() {
    if (navigator.storage && navigator.storage.persist) {
        const alreadyPersisted = await navigator.storage.persisted();
        if (alreadyPersisted) return true;

        const granted = await navigator.storage.persist();
        return granted;
    }
    return false;
}

void setup();
